// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// cc-common imports
import { DxWidgetUtils, IconConstants, Utils } from 'cc-common';

// devextreme imports
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import { formatNumber } from 'devextreme/localization';
import notify from 'devextreme/ui/notify';

// service imports
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { ApplyAdjustmentDebitService } from '../../_services/accounts-payable/apply-adjustment-debit.service';

// model imports
import { APApplyDebitParametersDto } from '../../_models/accounts-payable/apply-debit/ap-apply-debit-parameters-dto';
import { APCreditApplicationEditorParameterDto } from '../../_models/accounts-payable/apply-debit/ap-credit-application-editor-parameter-dto';
import { APCreditApplicationEditorResultDto } from '../../_models/accounts-payable/apply-debit/ap-credit-application-editor-result-dto';
import { APCreditApplicationSourceTransactionResultDto } from '../../_models/accounts-payable/apply-debit/ap-credit-application-source-transaction-result-dto';
import { APCreditApplicationTargetParametersDto } from '../../_models/accounts-payable/apply-debit/ap-credit-application-target-parameters-dto';
import { APCreditApplicationTargetTransactionResultDto } from '../../_models/accounts-payable/apply-debit/ap-credit-application-target-transaction-result-dto';
import { APCreditApplicationTransactionItemResultDto } from '../../_models/accounts-payable/apply-debit/ap-credit-application-transaction-item-result-dto';
import { DimensionOptionCodeRawDto } from '../../_models/common/dimensions/dimension-option-code-raw-dto';

// constant imports
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';

@Component({
    selector: 'app-apply-adjustment-debit',
    templateUrl: './apply-adjustment-debit.component.html',
    styleUrls: ['./apply-adjustment-debit.component.scss']
})
export class ApplyAdjustmentDebitComponent implements OnInit {
    @ViewChild('applyDebitEditorPopup', { static: false }) applyDebitEditorPopup: DxPopupComponent;
    @ViewChild('targetTransactionDataGrid', { static: false }) targetTransactionDataGrid: DxDataGridComponent;

    @Input() communityId: number;
    @Input() managementCompanyId: number;
    @Input() transactionId: number;
    @Input() requiredDimensionOptions: Array<DimensionOptionCodeRawDto>;

    @Output() onClose = new EventEmitter<boolean>();

    visible = false;
    isApplyDebited = false;

    popUpTitle: string;

    applyDebitEditor: APCreditApplicationEditorResultDto;
    sourceTransactions: APCreditApplicationSourceTransactionResultDto;
    targetTransactions: Array<APCreditApplicationTargetTransactionResultDto> = [];
    selectedTargetTransactionId: number;
    showLineItemAmountEditor = false;

    targetParams: Array<APCreditApplicationTargetParametersDto> = [];
    totalAppliedAmount = 0;
    remainingUnappliedAmount = 0;

    applyDebitParameter: APApplyDebitParametersDto = new APApplyDebitParametersDto();

    currencyFormat: string;
    dateFormat: string;

    iconCheck = IconConstants.CHECK;
    iconCancel = IconConstants.CANCEL;

    loadingVisible = false;

    constructor(
        private readonly applyDebitService: ApplyAdjustmentDebitService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService
    ) {}

    ngOnInit(): void {
        this.popUpTitle = `Apply Debit Applications of Adjustment # ${this.transactionId}`;
        this.currencyFormat = this.precisionService.getCurrencyFormat();
        this.dateFormat = StringFormatConstants.dateFormat;
        this.configureTargetParams();

        this.show();
        this.prepareApplyDebitEditor();
    }

    show(): void {
        this.visible = true;
    }

    onShown(): void {
        DxWidgetUtils.instantRepaint(this.applyDebitEditorPopup);
    }

    onHidden(): void {
        this.closeEditor(false);
    }

    getSourceTransactionLineItemDetail(row: any): string {
        return `Remaining Unapplied Amount Line ${row.rowIndex + 1}: ${formatNumber(
            row.data.amount,
            this.currencyFormat
        )}`;
    }

    getDimensionTitle(lineItem: APCreditApplicationTransactionItemResultDto): string {
        let dimensionTitle = '';
        lineItem.dimensions.forEach((dim) => {
            const index = lineItem.dimensions.indexOf(dim);
            dimensionTitle += `${dim.dimensionTitle}: ${dim.dimensionOptionTitle}`;
            if (Utils.notEquals(index, lineItem.dimensions.length - 1)) {
                dimensionTitle += ', ';
            }
        });
        return dimensionTitle;
    }

    onGridSelectionChange(): void {
        const selectedRows = this.targetTransactionDataGrid.instance.getSelectedRowsData();
        let totalAmount = 0;
        selectedRows.forEach((item) => {
            if (item.appliedAmount > 0) {
                totalAmount += item.appliedAmount;
            } else {
                this.selectedTargetTransactionId = item.id;
                this.showLineItemAmountEditor = true;
            }
        });

        totalAmount = this.precisionService.getRoundedNumber(totalAmount);
        this.totalAppliedAmount = totalAmount;
        this.remainingUnappliedAmount = this.precisionService.getRoundedNumber(
            this.sourceTransactions.unappliedAmount - totalAmount
        );
    }

    onEditingStart(event: any): void {
        const rowData = event.data;
        if (Utils.equalsIgnoreCase(event.column.dataField, 'appliedAmount')) {
            this.selectedTargetTransactionId = rowData.id;
            this.showLineItemAmountEditor = true;
        }
    }

    onLineItemAmountEditorClose(): void {
        this.showLineItemAmountEditor = false;
    }

    onLineItemAmountEditorSave(targetItem: APCreditApplicationTargetParametersDto): void {
        this.targetParams.push(targetItem);
        let totalAmountAppliedForTarget = 0;
        let totalAppliedAmount = 0;
        targetItem.items.forEach((item) => {
            totalAmountAppliedForTarget += item.applyAmount;
        });

        this.targetTransactions.forEach((transaction) => {
            if (Utils.equals(transaction.id, targetItem.targetId)) {
                transaction.appliedAmount = this.precisionService.getRoundedNumber(totalAmountAppliedForTarget);
                this.targetTransactionDataGrid.instance.repaintRows([this.targetTransactions.indexOf(transaction)]);
            }
            totalAppliedAmount += transaction.appliedAmount;
        });
        this.targetTransactionDataGrid.instance.selectRows([targetItem.targetId], true);
        this.totalAppliedAmount = this.precisionService.getRoundedNumber(totalAppliedAmount);
    }

    onApplyClick(): void {
        this.applyDebitParameter.targets = this.targetParams;
        if (Utils.notNullOrEmpty(this.requiredDimensionOptions)) {
            this.applyDebitParameter.requiredDimensions = this.requiredDimensionOptions;
        }
        this.applyDebitAdjustment();
    }

    onCancelClick(): void {
        this.closeEditor(false);
    }

    private prepareApplyDebitEditor(): void {
        const baseParams: APCreditApplicationEditorParameterDto = new APCreditApplicationEditorParameterDto();
        baseParams.communityId = this.communityId;
        baseParams.managementCompanyId = this.managementCompanyId;
        baseParams.transactionId = this.transactionId;
        if (Utils.notNullOrEmpty(this.requiredDimensionOptions)) {
            baseParams.requiredDimensions = this.requiredDimensionOptions;
        }
        this.applyDebitService.getApplyDebitEditorData(baseParams).subscribe(
            (result: APCreditApplicationEditorResultDto) => {
                this.applyDebitEditor = result;
                this.sourceTransactions = result.source;
                this.targetTransactions = result.targets;
                this.remainingUnappliedAmount = result.source.unappliedAmount;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private applyDebitAdjustment(): void {
        this.loadingVisible = true;
        this.applyDebitService.applyDebitToAdjustment(this.applyDebitParameter).subscribe(
            () => {
                const message = `Apply Debit Applications for Adjustment # ${this.transactionId} went successfull`;
                notify(message, 'Success', MessageInterval.successMsgInterval);
                this.isApplyDebited = true;
                this.loadingVisible = false;

                this.closeEditor(true);
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private configureTargetParams(): void {
        this.applyDebitParameter.applyDate = new Date();
        this.applyDebitParameter.sourceId = this.transactionId;
        this.applyDebitParameter.communityId = this.communityId;
        this.applyDebitParameter.managementCompanyId = this.managementCompanyId;
    }

    private closeEditor(forceReload: boolean): void {
        this.visible = false;
        this.onClose.emit(forceReload);
    }
}
